// AuthContext using real Firebase Auth + Firestore user profile (role).
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setUser(fbUser);
        try {
          const snap = await getDoc(doc(db, "users", fbUser.uid));
          if (snap.exists()) {
            setUserData({ id: fbUser.uid, ...snap.data() });
          } else {
            // No profile doc yet — create a minimal customer profile.
            const profile = {
              email: fbUser.email,
              name: fbUser.displayName || "",
              phone: "",
              role: "customer",
              createdAt: new Date().toISOString(),
            };
            await setDoc(doc(db, "users", fbUser.uid), profile);
            setUserData({ id: fbUser.uid, ...profile });
          }
        } catch (e) {
          console.error("Load user profile error:", e);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const register = async (email, password, name, phone) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      try { await updateProfile(cred.user, { displayName: name }); } catch (_) {}
    }
    const profile = {
      email,
      name: name || "",
      phone: phone || "",
      role: "customer",
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, "users", cred.user.uid), profile);
    setUserData({ id: cred.user.uid, ...profile });
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  const logout = async () => {
    await signOut(auth);
  };

  const isAdmin = userData?.role === "admin";

  // Firebase-compat surface for legacy consumers (user.uid / user.email).
  const compatUser = user
    ? {
        uid: user.uid,
        email: user.email,
        displayName: userData?.name || user.displayName || "",
      }
    : null;

  return (
    <AuthContext.Provider
      value={{ user: compatUser, userData, loading, register, login, logout, isAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
};
